"use client";

import { usePassportProvider } from "@/context/PassportProvider";
import { useGetWishlistedGames } from "@/hooks/api";
import { type PropsWithChildren, createContext, useContext } from "react";

type GlobalWishlistContextType = {
  wishlistedGames: string[];
  isLoading: boolean;
};

const GlobalWishlistContext = createContext<GlobalWishlistContextType>({
  wishlistedGames: [],
  isLoading: false,
});

/**
 * Provider that fetches the user's wishlist ONCE at a high level
 * to avoid 429 rate limiting when multiple components check wishlist status
 */
export function GlobalWishlistProvider({ children }: PropsWithChildren) {
  const { passportState, walletAddress } = usePassportProvider();

  const { wishlistedGames, isLoading } = useGetWishlistedGames({
    walletAddress: walletAddress || null,
    isPassportWallet: passportState.authenticated && passportState.ready,
  });

  return (
    <GlobalWishlistContext.Provider
      value={{
        wishlistedGames: wishlistedGames || [],
        isLoading,
      }}
    >
      {children}
    </GlobalWishlistContext.Provider>
  );
}

export function useGlobalWishlist() {
  return useContext(GlobalWishlistContext);
}
